import React from 'react'
import styled from 'styled-components'
import { LandUsePlanDocument } from '@popety-io/types'
import { PDFViewer } from '../../../../../organisms/PDFViewer'
import { Icon } from '../../../../../atoms/Icon'
import { Text } from '../../../../../atoms/Text'

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${({ theme }) => theme.colors.background.secondary};
`

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: ${({ theme }) => theme.spacing.huge};
  text-align: center;
`

const EmptyStateIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: ${({ theme }) => theme.borderRadius.circle};
  background: ${({ theme }) => theme.colors.background.tertiary};
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-bottom: ${({ theme }) => theme.spacing.large};
`

const EmptyStateTitle = styled(Text)`
  font-size: ${({ theme }) => theme.typography.fontSize.title};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.small};
`

const EmptyStateDescription = styled(Text)`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 400px;
`

const DocumentHeader = styled.div`
  padding: ${({ theme }) => theme.spacing.default};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  background: ${({ theme }) => theme.colors.background.primary};
`

const DocumentTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.xsmall} 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const DocumentMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
`

const ImportantBadge = styled.span`
  padding: 2px 6px;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background: ${({ theme }) => theme.colors.warning}20;
  color: ${({ theme }) => theme.colors.warning};
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  text-transform: uppercase;
`

const PDFContainer = styled.div`
  flex: 1;
  overflow: hidden;
`

export interface PDFViewerPanelTranslations {
  noDocumentSelected?: string
  selectDocumentHint?: string
  important?: string
  noPreviewAvailable?: string
  noPreviewHint?: string
  documentTypes?: {
    rule?: string
    map?: string
  }
}

const DEFAULT_TRANSLATIONS: PDFViewerPanelTranslations = {
  noDocumentSelected: 'No Document Selected',
  selectDocumentHint: 'Select a document from the list to view its contents',
  important: 'Important',
  noPreviewAvailable: 'No Preview Available',
  noPreviewHint: 'This document does not have a PDF preview URL available',
  documentTypes: {
    rule: 'rule',
    map: 'map',
  },
}

interface PDFViewerPanelProps {
  document: LandUsePlanDocument | null
  translations?: PDFViewerPanelTranslations
  locale?: string
}

const formatDate = (dateString?: string, locale?: string): string => {
  if (!dateString) return ''
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString(locale || 'fr-CH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  } catch {
    return ''
  }
}

export const PDFViewerPanel: React.FC<PDFViewerPanelProps> = ({
  document,
  translations,
  locale,
}) => {
  const t = {
    ...DEFAULT_TRANSLATIONS,
    ...translations,
    documentTypes: {
      ...DEFAULT_TRANSLATIONS.documentTypes,
      ...translations?.documentTypes,
    },
  }

  // Translate document type
  const getTranslatedDocumentType = (docType?: string): string => {
    if (!docType) return ''
    return t.documentTypes?.[docType as keyof typeof t.documentTypes] || docType
  }

  if (!document) {
    return (
      <PanelContainer>
        <EmptyStateContainer>
          <EmptyStateIcon>
            <Icon name="file-text" size="large" />
          </EmptyStateIcon>
          <EmptyStateTitle>{t.noDocumentSelected}</EmptyStateTitle>
          <EmptyStateDescription>{t.selectDocumentHint}</EmptyStateDescription>
        </EmptyStateContainer>
      </PanelContainer>
    )
  }

  const pdfUrl = document.url

  // If document doesn't have a PDF URL, show message
  if (!pdfUrl) {
    return (
      <PanelContainer>
        <DocumentHeader>
          <DocumentTitle>{document.title}</DocumentTitle>
          {document.important && <ImportantBadge>{t.important}</ImportantBadge>}
        </DocumentHeader>
        <EmptyStateContainer>
          <EmptyStateIcon>
            <Icon name="alert-circle" size="large" />
          </EmptyStateIcon>
          <EmptyStateTitle>{t.noPreviewAvailable}</EmptyStateTitle>
          <EmptyStateDescription>{t.noPreviewHint}</EmptyStateDescription>
        </EmptyStateContainer>
      </PanelContainer>
    )
  }

  const metaParts = []
  if (document.document_type) {
    metaParts.push(getTranslatedDocumentType(document.document_type))
  }
  if (document.publication_date) {
    metaParts.push(formatDate(document.publication_date, locale))
  }
  if (document.language) {
    metaParts.push(document.language.toUpperCase())
  }

  return (
    <PanelContainer>
      <DocumentHeader>
        <DocumentTitle>{document.title}</DocumentTitle>
        <DocumentMeta>
          {document.important && <ImportantBadge>{t.important}</ImportantBadge>}
          {metaParts.length > 0 && <span>{metaParts.join(' • ')}</span>}
        </DocumentMeta>
      </DocumentHeader>

      <PDFContainer>
        <PDFViewer
          url={pdfUrl}
          height="100%"
          width="100%"
          showThumbnails={false}
          showToolbar={true}
          enableSearch={true}
          enableDownload={true}
        />
      </PDFContainer>
    </PanelContainer>
  )
}
